import { Component, inject, OnDestroy, signal, TemplateRef, viewChild } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import {
  dateOnlyFormat,
  dateTimeFormat,
  dateTimeWithoutTimezone,
  est,
  formatAsUTC,
  tzDate,
  utc,
} from 'src/app/shared/utilities/date'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { DataTableComponent } from '../table/data-table.component'
import {
  ScheduledEvent,
  ScheduleMDMASettlementRunEvent,
  ScheduleTBillSettlementRunEvent,
  SchedulingService,
} from '../scheduleing/scheduling.service'
import { ColDef, GetRowIdFunc, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import { combineLatest, from, Subscription, switchMap } from 'rxjs'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { TableHeaderComponent, withHeader } from '../table/headers/table-header.component'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { EntityFilterModalComponent } from '../modals/filters/entity-filter-modal.component'
import { ActivatedRoute, Router } from '@angular/router'
import { KeyValuePipe, Location } from '@angular/common'
import { ToastService } from '../toasts/toasts.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { StringPipe } from 'src/app/shared/pipes/string.pipe'
import { MatMenuModule } from '@angular/material/menu'
import {
  ScheduleMDMASettlementsRunModalComponent,
} from '../modals/schedule-mdma-settlements-run-modal/schedule-mdma-settlements-run-modal.component'
import { TableSortService } from '../table/services/table-sort.service'
import openModal from 'src/app/shared/services/openModal'
import {
  ScheduleTBillSettlementsRunModalComponent,
} from '../modals/schedule-tbill-settlements-run-modal/schedule-tbill-settlements-run-modal.component'
import { DateCellComponent } from '../table/cells/date-cell.component'
import { actionColumn, withCell } from '../table/cells/with-cell'
import { pipe } from 'lodash/fp'
import { EntityCellComponent } from '../table/cells/entity-cell.component'
import { LabeledDisplayComponent } from '../labeled-display/labeled-display.component'
import { DateDisplayComponent } from '../date-display/date-display.component'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { StringFilter } from '../../shared/services/filter/string-filter'
import { orderBy } from 'lodash'
import { formatUser, getUserFilterConfig } from '../../shared/utilities/user'

type ScheduleModal = ScheduleMDMASettlementsRunModalComponent | ScheduleTBillSettlementsRunModalComponent

@Component({
  selector: 'app-settlement-run-schedule',
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    LabeledPropertyComponent,
    LabeledDisplayComponent,
    DateDisplayComponent,
    KeyValuePipe,
    StringPipe,
  ],
  providers: [TableSortService],
  styles: '',
  template: `
    <div app-page-header header="Scheduled Settlement Runs" [menuItems]="pageHeaderMenuItems"></div>
    <div app-page-content>
      <app-active-filter-list [filterGroup]="constructor.name"/>
      <app-data-table
          heightStyle="calc(100vh - 270px)"
          [data]="data()"
          [getRowId]="getRowId"
          [columnDefs]="columnDefs"
      />
    </div>

    <ng-template #deleteConfirmation let-ctx="ctx">
      Are you sure you want to delete scheduled run?
      @if (ctx) {
        <div app-labeled-property label="Category">
          <div app-labeled-display [value]="ctx.category"></div>
        </div>
        <div app-labeled-property label="Scheduled Time">
          <div app-date-display [value]="ctx.event_timestamp" [format]="dateTimeFormat"
              viewAsTZ="America/Chicago"></div>
        </div>
        @for (param of ctx.parameters | keyvalue; track param.key) {
          <div app-labeled-property [label]="param.key | string">{{ param.value }}</div>
        }
      }
    </ng-template>
  `,
})
export class SettlementRunScheduleComponent implements OnDestroy {
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)
  private readonly toastService = inject(ToastService)
  private readonly modalService = inject(NgbModal)
  private readonly filterService = inject(FilterService)
  private readonly schedulingService = inject(SchedulingService)
  private readonly sortService = inject(TableSortService)

  private readonly subscription = new Subscription()

  private readonly deleteConfirmationTemplate = viewChild<TemplateRef<{ ctx: ScheduledEvent }>>('deleteConfirmation')

  protected readonly dateTimeFormat = dateTimeFormat

  protected readonly refresh = signal(0)
  protected readonly data = signal<ScheduledEvent[]>([])

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Schedule MDMA Settlement', onClick: () => this.openScheduleMDMASettlementModal() },
    { label: 'Schedule TBill Settlement', onClick: () => this.openScheduleTBillSettlementModal() },
  ]

  protected readonly getRowId: GetRowIdFunc<ScheduledEvent> = (data) => data.data.id
  protected readonly columnDefs: ColDef[] = [
    pipe(
      withHeader<ScheduledEvent, EntityFilterModalComponent>({
        headerName: 'Event Status',
        field: 'event_status',
        filterable: false,
        sortable: true,
        filterGroup: SettlementRunScheduleComponent.name,
        modal: EntityFilterModalComponent,
        modalInit: {},
      }),
      withCell<EntityCellComponent<ScheduledEvent, 'event_status'>>(
        EntityCellComponent,
        {
          value: (d) => d.event_status.toUpperCase(),
        },
      ))({
      flex: 1,
      editable: false,
      minWidth: 150,
      width: 150,
    }),
    pipe(
      withHeader<ScheduledEvent, EntityFilterModalComponent>({
        headerName: 'Category',
        field: 'category',
        filterable: true,
        sortable: true,
        filterGroup: SettlementRunScheduleComponent.name,
        modal: EntityFilterModalComponent,
        modalInit: {},
      }),
      withCell<EntityCellComponent<ScheduledEvent, 'category'>>(
        EntityCellComponent,
        {
          value: (d) => d.category.toUpperCase(),
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 180,
    }),
    pipe(
      withHeader<ScheduledEvent, DateRangeFilterModalComponent>({
        headerName: 'Scheduled Time (STL)',
        field: 'event_timestamp',
        filterable: true,
        sortable: true,
        filterGroup: SettlementRunScheduleComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<ScheduledEvent>>(
        DateCellComponent,
        {
          date: (d) => d.event_timestamp,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    {
      field: 'user_name',
      width: 150,
      minWidth: 100,
      headerName: 'User',
      filter: true,
      sortable: true,
      editable: false,
      cellRenderer: ({ value }: ICellRendererParams<ScheduledEvent, string>) => formatUser(value, 'n/a'),
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: SettlementRunScheduleComponent.name,
        modal: StringFilterModalComponent,
        modalOptions: { centered: true },
      },
    },
    withHeader<ScheduledEvent>({
      headerName: 'Parameters',
      field: 'parameters',
      filterGroup: SettlementRunScheduleComponent.name,
    })({
      editable: false,
      minWidth: 100,
      width: 600,
      valueGetter: ({ data }: ValueGetterParams<ScheduledEvent>) =>
        Object.entries(data?.parameters ?? {}).map(([k, v]) =>
          `${k.toUpperCase()}=${this.formatParameter(k, v)}`).join(', ',
        )
      ,
    }),
    actionColumn<ScheduledEvent>(SettlementRunScheduleComponent.name, {
      actions: [
        {
          name: 'Delete',
          onAction: (evt) => this.openDeleteConfirmationModal(evt),
          disabled: (evt) => evt.event_status !== 'NEW',
          sensitive: true,
        },
      ],
    }),
  ]

  constructor() {
    const { dispatch } = this.filterService
    dispatch(FilterService.InitFiltersAction({
      fieldGroup: SettlementRunScheduleComponent.name,
      fields: {
        category: new EntityFilter({
          config: {
            resource: 'scheduledEventCategory',
            allowMultiple: true,
            allowWildcards: false,
            meta: {
              fieldGroup: SettlementRunScheduleComponent.name,
              fieldName: 'category',
              fieldDisplay: 'Category',
            },
          },
        }, dispatch),
        event_timestamp: new DateRangeFilter({
          operation: 'between',
          value: [
            utc(startOfMonth(est())),
            utc(endOfMonth(est())),
          ],
          config: {
            meta: {
              fieldGroup: SettlementRunScheduleComponent.name,
              fieldName: 'event_timestamp',
              fieldDisplay: 'Scheduled Time (STL)',
            },
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            constant: true,
            viewAsTZ: 'America/Chicago',
          },
        }, dispatch),
        user_name: new StringFilter(
          { config: getUserFilterConfig(SettlementRunScheduleComponent.name, 'user_name', 'user') },
          dispatch,
        ),
      },
    }))

    this.subscription.add(
      combineLatest([
        this.filterService.asParams(SettlementRunScheduleComponent.name),
        toObservable(this.refresh),
      ]).pipe(
        takeUntilDestroyed(),
        switchMap(([params]) => this.schedulingService.get(params)),
      ).subscribe({
        error: e => {
          this.data.set([])
          this.toastService.showError(e)
        },
        next: (res) => {
          this.data.set(this.sort(res))
        },
      }),
    )

    this.subscription.add(
      from(this.filterService.manageQueryParms(
        this.router,
        this.route,
        this.location,
        SettlementRunScheduleComponent.name,
      )).pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      ).subscribe(),
    )
    this.sortService.setSortOrder([{ name: 'event_timestamp', direction: 'desc' }])
    this.subscription.add(
      this.sortService.getOrder().subscribe(() => {
        this.data.set(this.sort(this.data()))
      }),
    )
  }

  private formatParameter(key: string, value: string): string {
    if (['billing_month', 'operating_date', 'operating_day'].includes(key)) {
      const dateStr = 
        /^\d{4}-\d{2}-\d{2}$/.test(value) 
          ? value + 'T05:00:00'
          : /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}Z{0,1}$/.test(value) 
            ? value.replace(' ', 'T').replace('Z', '')
            : undefined
      try{
        return dateStr 
          ? format(est(tzDate(dateStr, '+00')), dateOnlyFormat)
          : value
      } catch {
        return value
      }
    } else {
      return value
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
  }

  protected sort(data: ScheduledEvent[]): ScheduledEvent[] {
    const sortOrder = this.sortService.getSortOrder()
    if (sortOrder.length > 0) {
      return orderBy(
        data,
        sortOrder.map(_ => _.name),
        sortOrder.map(_ => _.direction ?? 'asc'),
      )
    } else {
      return data
    }
  }

  protected openScheduleMDMASettlementModal() {
    this.openScheduleModal(ScheduleMDMASettlementsRunModalComponent)
  }

  protected openScheduleTBillSettlementModal() {
    this.openScheduleModal(ScheduleTBillSettlementsRunModalComponent)
  }

  private openScheduleModal<T extends ScheduleModal>(Constructor: new () => T) {
    openModal(this.modalService, Constructor).init({
      onCancel: () => {
        this.modalService.dismissAll()
      },
      onSchedule: evt => {
        this.onSchedule(evt)
      },
    })
  }


  openDeleteConfirmationModal(evt: ScheduledEvent) {
    openModal(this.modalService, ConfirmationModalComponent<ScheduledEvent>).init({
      headerText: 'Confirm Deletion',
      confirmButtonText: 'Delete',
      bodyTemplate: this.deleteConfirmationTemplate(),
      bodyContext: evt,
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onConfirm: (() => {
        this.onDelete(evt)
      }),
    })
  }

  private onDelete(evt: ScheduledEvent) {
    this.schedulingService
      .delete(evt.category, evt.id)
      .subscribe({
        error: e => {
          this.toastService.showError(e)
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Scheduled run was deleted successfully' })
          this.refresh.update(v => v + 1)
          this.modalService.dismissAll()
        },
      })
  }

  private onSchedule(evt: ScheduleMDMASettlementRunEvent | ScheduleTBillSettlementRunEvent) {
    this.schedulingService
      .schedule({
        category: evt.category,
        event_timestamp: formatAsUTC(evt.event_timestamp),
        parameters: {
          settlement_type: evt.settlement_type,
          operating_date: formatAsUTC((evt.operating_date), false),
          ...evt.category === 'tbill_aggregation'
            ? { billing_month: formatAsUTC((evt.billing_month), false) }
            : {},
        },
      })
      .subscribe({
        next: () => {
          this.toastService.show({ type: 'success', message: 'Scheduled run successfully' })
          this.refresh.update(v => v + 1)
          this.modalService.dismissAll()
        },
        error: e => {
          this.toastService.showError(e)
        },
      })
  }
}
